/**
 * Client-side image resizing utility.
 * Resizes large images before S3 upload to save bandwidth and storage.
 * Converts HEIC/HEIF files to JPEG since browsers can't display them natively.
 */

import type { RcFile } from 'antd/es/upload';

interface ResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1, JPEG quality
}

const DEFAULTS: Required<ResizeOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.82,
};

/**
 * Detect HEIC/HEIF by file extension (browsers often report empty MIME for these).
 */
function isHeic(file: File): boolean {
  const ext = file.name?.split('.').pop()?.toLowerCase() || '';
  return ext === 'heic' || ext === 'heif';
}

/**
 * Convert a HEIC/HEIF file to a JPEG Blob using heic2any.
 * Dynamically imported so the library isn't bundled unless needed.
 */
async function convertHeicToJpeg(file: File): Promise<Blob> {
  const heic2any = (await import('heic2any')).default;
  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92, // High quality — we'll resize + compress after
  });
  // heic2any can return a single Blob or an array
  return Array.isArray(result) ? result[0] : result;
}

/**
 * Load a Blob into an HTMLImageElement and return it once decoded.
 */
function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for resizing'));
    };
    img.src = url;
  });
}

/**
 * Draw an image onto a canvas at the target dimensions and export as JPEG blob.
 */
function canvasResize(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'));
      return;
    }

    // Use high-quality downscaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Resize an image file for upload.
 *
 * - Converts HEIC/HEIF → JPEG automatically
 * - Skips resize if the image is already within max dimensions
 * - Preserves the `uid` property for Ant Design Upload tracking
 * - Returns the original file unchanged if no processing is needed
 */
export async function resizeImage(
  file: RcFile | File,
  options?: ResizeOptions
): Promise<RcFile> {
  const { maxWidth, maxHeight, quality } = { ...DEFAULTS, ...options };

  let blob: Blob = file;
  let wasHeic = false;

  // Step 1: Convert HEIC → JPEG if needed
  if (isHeic(file)) {
    try {
      blob = await convertHeicToJpeg(file);
      wasHeic = true;
    } catch (err) {
      console.error('HEIC conversion failed, uploading original file:', err);
      // Can't convert and Chrome can't render HEIC — return original file as-is
      // The server/imgix CDN will handle the conversion on display
      return file as RcFile;
    }
  }

  // Step 2: Load into an Image element to read dimensions
  let img: HTMLImageElement;
  try {
    img = await loadImage(blob);
  } catch {
    console.warn('Could not load image for resizing, uploading original file');
    return file as RcFile;
  }
  const { naturalWidth: w, naturalHeight: h } = img;

  // Step 3: Check if resize is needed
  const needsResize = w > maxWidth || h > maxHeight;

  if (!needsResize && !wasHeic) {
    // Image is small enough and not HEIC — return original unchanged
    return file as RcFile;
  }

  // Step 4: Calculate target dimensions (fit within maxWidth × maxHeight)
  let targetW = w;
  let targetH = h;

  if (needsResize) {
    const ratio = Math.min(maxWidth / w, maxHeight / h);
    targetW = Math.round(w * ratio);
    targetH = Math.round(h * ratio);
  }

  // Step 5: Resize via canvas
  const resizedBlob = await canvasResize(img, targetW, targetH, quality);

  // Step 6: Create a new File with correct metadata
  const originalName = file.name || 'image.jpg';
  const newName = originalName.replace(/\.(heic|heif)$/i, '.jpg');
  const resizedFile = new File([resizedBlob], newName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  }) as RcFile;

  // Preserve Ant Design's uid for progress tracking
  if ('uid' in file) {
    resizedFile.uid = (file as RcFile).uid;
  }

  const savings = file.size > 0
    ? Math.round((1 - resizedFile.size / file.size) * 100)
    : 0;
  console.log(
    `[imageResize] ${originalName}: ${w}×${h} → ${targetW}×${targetH}, ` +
    `${(file.size / 1024).toFixed(0)}KB → ${(resizedFile.size / 1024).toFixed(0)}KB (${savings}% smaller)`
  );

  return resizedFile;
}
